import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { changesSaved, somethingWentWrong } from "@/helpers/utilities";
import axios from "axios";
import { getAxiosConfig, getAxiosConfigFormData } from "@/helpers/axiosConfigHelpers";
import { removeToken } from "@/helpers/localStorageHelper";
import { usePage } from "@inertiajs/inertia-vue3";

export const useProfileStore = defineStore("profileStore", () => {
  // State
  const loading = ref(false);
  const loadingProfile = ref(false);
  const loadingViewSettingsProfile = ref(false);
  const loadingImage = ref(false);
  const viewsSettingsProfile = ref({});
  const contractorId = ref(0);
  const status = ref("");
  const profile = ref({});
  const loadFirstPageWithNoPreserve = ref(false);
  const mustVerifyEmail = ref(false);
  const activeProfileTab = ref(0);
  const commentId = ref(0);
  const replyId = ref(0);
  const reply = ref({});
  const pusherCommentPosted = ref({});
  const pusherCommentToDelete = ref({});
  const pusherComment = ref({});
  const postComment = ref({});
  const postReply = ref({});
  const updatedPost = ref({});
  const updatedPostId = ref(0);
  const deletedPost = ref({});
  const isCommentAdded = ref(false);
  const isCommentAddedEnlarged = ref(false);

  // Getters (computed)
  const getStatus = computed(() => status.value);
  const getCommentId = computed(() => commentId.value);
  const getIsCommentAdded = computed(() => isCommentAdded.value);
  const getIsCommentAddedEnlarged = computed(() => isCommentAddedEnlarged.value);
  const getPusherComment = computed(() => pusherComment.value);
  const getPusherCommentPosted = computed(() => pusherCommentPosted.value);
  const getPusherCommentToDelete = computed(() => pusherCommentToDelete.value);
  const getPostComment = computed(() => postComment.value);
  const getPostReply = computed(() => postReply.value);
  const getReplyId = computed(() => replyId.value);
  const getReply = computed(() => reply.value);
  const getActiveProfileTab = computed(() => activeProfileTab.value);
  const getLoadFirstPageWithNoPreserve = computed(() => loadFirstPageWithNoPreserve.value);
  const getLoading = computed(() => loading.value);
  const getLoadingProfile = computed(() => loadingProfile.value);
  const getLoadingViewSettingsProfile = computed(() => loadingViewSettingsProfile.value);
  const getViewsSettingsProfile = computed(() => viewsSettingsProfile.value);
  const getProfile = computed(() => profile.value);
  const getMustVerifyEmail = computed(() => mustVerifyEmail.value);
  const getUpdatedPost = computed(() => updatedPost.value);
  const getUpdatedPostId = computed(() => updatedPostId.value);
  const getDeletedPost = computed(() => deletedPost.value);

  // Actions
  const getViewSettingsProfile = async () => {
    loadingViewSettingsProfile.value = true;
    try {
      const response = await axios.get(
        "/api/profile/trades-views-settings",
        getAxiosConfig()
      );
      if (response.data) {
        viewsSettingsProfile.value = response.data.profile;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loadingViewSettingsProfile.value = false;
    }
  };

  const fetchComment = async (payload) => {
    const comment_id = payload.comment.parent_id
      ? payload.comment.parent_id
      : payload.comment.id;
    try {
      const response = await axios.get(
        `/api/posts/comments/${comment_id}`,
        getAxiosConfig()
      );
      if (response.data) {
        pusherComment.value = response.data;
      }
    } catch (err) {
      somethingWentWrong();
    }
  };

  const fetchCommentPosted = async (payload) => {
    const comment_id = payload.comment.id
      ? payload.comment.id
      : payload.comment.parent_id;
    try {
      const response = await axios.get(
        `/api/posts/comments/${comment_id}`,
        getAxiosConfig()
      );
      if (response.data) {
        pusherCommentPosted.value = response.data;
      }
    } catch (err) {
      somethingWentWrong();
    }
  };

  const fetchProfile = async (forLoadingImage = false) => {
    loading.value = true;
    try {
      const response = await axios.get(`/api/profile`, getAxiosConfig());
      if (response.data) {
        profile.value = response.data.profile;
      }
    } catch (err) {
      removeToken();
      throw err;
      // somethingWentWrong();
    } finally {
      loading.value = false;
      if (forLoadingImage) {
        loadingImage.value = false;
      }
    }
  };

  const updateTrades = async (form) => {
    loading.value = true;
    try {
      await axios.patch(
        `/api/profile/trades`,
        form,
        getAxiosConfig()
      );
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const updateProfileSetupTrades = async (form) => {
    loading.value = true;
    try {
      await axios.patch(
        `/api/profile/trades-profile-setup`,
        form,
        getAxiosConfig()
      );
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const updateViewSettingsTrades = async (form) => {
    loading.value = true;
    try {
      const response = await axios.patch(
        `/api/profile/trades-views-settings`,
        form,
        getAxiosConfig()
      );
      if (response.data) {
        loadFirstPageWithNoPreserve.value = true;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const updateViews = async (form) => {
    loading.value = true;
    try {
      await axios.patch(
        `/api/profile/views`,
        form,
        getAxiosConfig()
      );
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const updateProfileSetupViews = async (form) => {
    loading.value = true;
    try {
      await axios.patch(
        `/api/profile/views-profile-setup`,
        form,
        getAxiosConfig()
      );
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const updateViewSettingsViews = async (form) => {
    loading.value = true;
    try {
      const response = await axios.patch(
        `/api/profile/views-views-settings`,
        form,
        getAxiosConfig()
      );
      if (response.data) {
        loadFirstPageWithNoPreserve.value = true;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const updateProfileGeneralInfo = async (payload) => {
    loading.value = true;
    try {
      const response = await axios.patch(
        `/api/profile/all-basic-info`,
        payload.form,
        getAxiosConfig()
      );
      if (response.data && payload.showSuccess) {
        changesSaved("Changes Successfully Saved");
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const updateProfileGeneralInfoForProfileSetup = async (payload) => {
    loading.value = true;
    try {
      const response = await axios.patch(
        `/api/profile/all-basic-info-for-profile-setup`,
        payload.form,
        getAxiosConfig()
      );
      if (response.data && payload.showSuccess) {
        changesSaved("Changes Successfully Saved");
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const completeProfileAndStartFreeSubscription = async () => {
    try {
      const response = await axios.post(
        `/api/profile/complete-profile-and-start-free-subscription`,
        {},
        getAxiosConfig()
      )
      return response;
    } catch (error) {
      somethingWentWrong(error.response.data.message);
      throw error
    }
  };

  const uploadCompanyLogo = async (formData) => {
    try {
      const response = await axios.post(
        '/api/profile/company-logo',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-CSRF-TOKEN': usePage().props.value.csrf_token,
          },
        }
      )
      return response;
    } catch (error) {
      console.error('Error uploading logo:', error)
      somethingWentWrong(error.response.data.message);
      throw error
    }
  };

  const uploadUserAvatar = async (formData) => {
  try {
    const response = await axios.post('/api/profile/user-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-CSRF-TOKEN': usePage().props.value.csrf_token,
      },
    });

    return response;
  } catch (error) {
    console.error('Failed to upload user avatar:', error);
    somethingWentWrong(error.response.data.message);
    throw error;
  }
};

  // Mutations (as setters)
  const setStatus = (payload) => { status.value = payload; };
  const setIsCommentAdded = (isAdded) => { isCommentAdded.value = isAdded; };
  const setIsCommentAddedEnlarged = (isAdded) => { isCommentAddedEnlarged.value = isAdded; };
  const setCommentId = (payload) => { commentId.value = payload; };
  const setPostComment = (payload) => { postComment.value = payload; };
  const setPusherComment = (payload) => { pusherComment.value = payload; };
  const setPusherCommentPosted = (payload) => { pusherCommentPosted.value = payload; };
  const setPusherCommentToDelete = (payload) => { pusherCommentToDelete.value = payload; };
  const setPostReply = (payload) => { postReply.value = payload; };
  const setReplyId = (payload) => { replyId.value = payload; };
  const setReply = (payload) => { reply.value = payload; };
  const setContractorId = (payload) => { contractorId.value = payload; };
  const setProfile = (payload) => { profile.value = payload; };
  const setActiveTab = (payload) => { activeProfileTab.value = payload; };
  const setMustVerifyEmail = (payload) => { mustVerifyEmail.value = payload; };
  const setLoading = (payload) => { loading.value = payload; };
  const setLoadFirstPageWithNoPreserve = (payload) => {
    loadFirstPageWithNoPreserve.value = payload;
  };
  const setLoadingProfile = (payload) => { loadingProfile.value = payload; };
  const setLoadingImage = (payload) => { loadingImage.value = payload; };
  const setLoadingViewSettingsProfile = (payload) => { loadingViewSettingsProfile.value = payload; };
  const setViewsSettingsProfile = (payload) => { viewsSettingsProfile.value = payload; };
  const setUpdatedPost = (payload) => { updatedPost.value = payload; };
  const setDeletedPost = (payload) => { deletedPost.value = payload; };
  const setUpdatedPostId = () => { updatedPostId.value = updatedPostId.value + 1; };

  return {
    // state
    loading,
    loadingProfile,
    loadingViewSettingsProfile,
    loadingImage,
    viewsSettingsProfile,
    contractorId,
    status,
    profile,
    loadFirstPageWithNoPreserve,
    mustVerifyEmail,
    activeProfileTab,
    commentId,
    replyId,
    reply,
    pusherCommentPosted,
    pusherCommentToDelete,
    pusherComment,
    postComment,
    postReply,
    updatedPost,
    updatedPostId,
    deletedPost,
    isCommentAdded,
    isCommentAddedEnlarged,
    // getters
    getStatus,
    getCommentId,
    getIsCommentAdded,
    getIsCommentAddedEnlarged,
    getPusherComment,
    getPusherCommentPosted,
    getPusherCommentToDelete,
    getPostComment,
    getPostReply,
    getReplyId,
    getReply,
    getActiveProfileTab,
    getLoadFirstPageWithNoPreserve,
    getLoading,
    getLoadingProfile,
    getLoadingViewSettingsProfile,
    getViewsSettingsProfile,
    getProfile,
    getMustVerifyEmail,
    getUpdatedPost,
    getUpdatedPostId,
    getDeletedPost,
    // actions
    getViewSettingsProfile,
    fetchComment,
    fetchCommentPosted,
    fetchProfile,
    updateTrades,
    updateProfileSetupTrades,
    updateViewSettingsTrades,
    updateViews,
    updateProfileSetupViews,
    updateViewSettingsViews,
    updateProfileGeneralInfo,
    updateProfileGeneralInfoForProfileSetup,
    completeProfileAndStartFreeSubscription,
    uploadCompanyLogo,
    uploadUserAvatar,
    // setters
    setStatus,
    setIsCommentAdded,
    setIsCommentAddedEnlarged,
    setCommentId,
    setPostComment,
    setPusherComment,
    setPusherCommentPosted,
    setPusherCommentToDelete,
    setPostReply,
    setReplyId,
    setReply,
    setContractorId,
    setProfile,
    setActiveTab,
    setMustVerifyEmail,
    setLoading,
    setLoadFirstPageWithNoPreserve,
    setLoadingProfile,
    setLoadingImage,
    setLoadingViewSettingsProfile,
    setViewsSettingsProfile,
    setUpdatedPost,
    setDeletedPost,
    setUpdatedPostId,

  };
},
  {
    persist: true
  }); 